import classNames from 'classnames';
import React from 'react';
import Button from '../Button';

export type CardProps = {
  children: React.ReactNode;
  href?: string;
  className?: string;
  role?: string;
  icon?: React.ReactNode;
  arrow?: boolean;
  tabIndex?: number;
  sticky?: boolean;
};

export type CardDescriptionProps = {
  children: React.ReactNode;
  className?: string;
};

type LinkItem = {
  title: string;
  url: string;
};

type ButtonLinkItem = {
  title: string;
  url: string;
};

export type CardTitleProps = {
  children: React.ReactNode;
  large?: boolean;
  medium?: boolean;
  charcoal?: boolean;
  className?: string;
  border?: boolean;
  link?: LinkItem;
  padding?: boolean;
  breadcrumbLink?: React.ReactNode;
  buttonLink?: ButtonLinkItem;
  columnSm?: boolean;
};

const Card: React.FC<CardProps> = ({
  children,
  className,
  href,
  sticky,
  ...rest
}) => {
  const classes = classNames(
    'card',
    rest.icon && 'card--with-icon',
    rest.arrow && 'card--with-arrow',
    sticky && 'card--sticky',
    className,
  );

  if (href) {
    return (
      <a href={href} className={classes} {...rest}>
        {rest.icon && <div className="card__icon">{rest.icon}</div>}
        {children}
      </a>
    );
  }

  return (
    <div className={classes} {...rest}>
      {rest.icon && <div className="card__icon">{rest.icon}</div>}
      {children}
    </div>
  );
};

const CardDescription: React.FC<CardDescriptionProps> = ({
  children,
  className,
  ...rest
}) => (
  <p className={classNames('card__description', className)} {...rest}>
    {children}
  </p>
);

const CardTitle: React.FC<CardTitleProps> = ({
  children,
  className,
  large,
  breadcrumbLink,
  medium,
  charcoal,
  border,
  link,
  padding = true,
  buttonLink,
  columnSm,
  ...rest
}) => {
  return (
    <div
      className={classNames(
        'card__title',
        large && 'card__title--large',
        medium && 'card__title--medium',
        charcoal && 'card__title--charcoal',
        border && 'card__title--border',
        buttonLink && 'card__title--has-button',
        padding && 'card__title--padding',
        columnSm && 'card__title--column-sm',
        className,
      )}
      {...rest}
    >
      {breadcrumbLink ? (
        <span>
          <small className="card__title-breadcrumb">{breadcrumbLink}</small>

          {children}
        </span>
      ) : (
        <>{children}</>
      )}

      {link && <a href={link.url}>{link.title}</a>}
      {buttonLink && (
        <Button tertiary alt small>
          {buttonLink.title}
        </Button>
      )}
    </div>
  );
};

export type CardInnerProps = {
  children: React.ReactNode;
  padding?: boolean;
  centered?: boolean;
};

const CardInner: React.FC<CardInnerProps> = ({
  children,
  padding = true,
  centered,
}) => {
  const classes = classNames(
    'card__inner',
    padding && 'card__inner--padding',
    centered && 'card__inner--centered',
  );

  return <div className={classes}>{children}</div>;
};

export { CardDescription, CardTitle, CardInner, Card };

export default Card;
